import { inject, Pipe, PipeTransform } from '@angular/core';
import { ApiService } from '@techextensor/tab-core-utility';
import { Constants } from '../const/constants';
import { firstValueFrom } from 'rxjs';
import { Observable } from 'rxjs';
import { HelperFunctionService } from '../services/helper-function.service';

@Pipe({
  name: 'AppIconUrlTransform',
  standalone: true
})
export class AppIconUrlTransformPipe implements PipeTransform {
  private readonly _apiService: ApiService = inject(ApiService);
  private readonly _helperFunctionService: HelperFunctionService = inject(HelperFunctionService);

  /**
   * Transforms the given value by processing the FTP URL.
   * @param value - The input value to transform.
   * @param defaultUrl - The default URL to return if processing fails.
   * @returns An Observable that emits the processed URL or the default URL.
   */
  transform(value: any): Observable<string> {    
    return new Observable((observer: any) => {
      // Process the FTP URL asynchronously
      this.processFtpUrl(value).then((url: string) => {
        // Emit the processed URL
        observer.next(url);
        // Complete the observable
        observer.complete();
      }).catch((error) => {
        // Log any errors and emit the default URL
        console.error('Error processing FTP URL:', error);
        this.fetchDefaultUrl().then((url: string) => {
          observer.next(url);
          observer.complete();
        })
      });
    });
  }

  /**
   * Process the given FTP URL asynchronously.
   * @param value - The input value to process.
   * @param defaultUrl - The default URL to return if processing fails.
   * @returns A Promise that resolves to the processed URL or the default URL.
   */
  private async processFtpUrl(value: any): Promise<string> {
    try {
      if (!value || typeof value !== 'string') {
        // Return the default URL if the input value is invalid
        return await this.fetchDefaultUrl();
      }

      const parsedValue: any = JSON.parse(value);
      const src = parsedValue.value;

      if (src && src.includes(Constants.FILEMANAGER_URL + Constants.GETIMAGE)) {

        // Get the default app headers
        const headers = this._helperFunctionService.getDefaultAppHeaders();

        // Get the blob from the server
        const blob = await firstValueFrom(this._apiService.getByUrl(src, { responseType: 'blob', headers }));

        // Create an object URL from the blob and return it
        const localUrl = URL.createObjectURL(blob);

        return localUrl;
      } else {
        
        if(src?.length) return src;

        // Return the default URL if the input value does not contain an FTP URL
        return await this.fetchDefaultUrl();
      }

    } catch (e) {
      // Log any errors and return the default URL
      console.error('Error processing FTP URL:', e);
      return await this.fetchDefaultUrl();
    }
  }

  private async fetchDefaultUrl() {
    const defaultBlob = await firstValueFrom(this._apiService.getByUrl('../../../assets/images/app/app-img.png', { responseType: 'blob' }));
    const defaultUrl = URL.createObjectURL(defaultBlob);
    return defaultUrl;
  }
}
